import React, { useState, useRef, useEffect, useCallback } from 'react';
import { RecordingSession, ParticipantTrack, NormalizedRect } from '@/types';
import { COMMON_LAYOUT_PRESETS, LayoutPreset, tileDetector } from '@/services/tileDetector';
import { formatDuration, generateId, downloadBlob, clamp } from '@/utils/helpers';
import confetti from 'canvas-confetti';
import {
  Users,
  User,
  Scissors,
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX,
  Check,
  CheckCircle2,
  Sparkles,
  Download,
  Plus,
  Trash2,
  Grid,
  Eye,
  Loader2,
  Share2,
  ChevronDown,
  CheckSquare,
  Square,
  UploadCloud,
  X
} from 'lucide-react';

interface SeparateVideosScreenProps {
  session: RecordingSession | null;
  recordings: RecordingSession[];
  onSelectSession: (session: RecordingSession) => void;
  onNavigateToEditor: () => void;
  onProceedToExport: () => void;
  onImportFile: () => void;
  onUpdateSession: (updated: RecordingSession) => void;
}

export const SeparateVideosScreen: React.FC<SeparateVideosScreenProps> = ({
  session,
  recordings,
  onSelectSession,
  onNavigateToEditor,
  onProceedToExport,
  onImportFile,
  onUpdateSession,
}) => {
  // If no session is selected or available
  if (!session) {
    return (
      <div className="flex-1 overflow-y-auto p-8 flex flex-col items-center justify-center text-center space-y-5 ambient-gradient select-none">
        <div className="w-16 h-16 rounded-3xl bg-brand-accent/20 border border-brand-accent/40 flex items-center justify-center shadow-glow">
          <Users className="w-8 h-8 text-brand-accent" />
        </div>
        <div className="space-y-1.5 max-w-md">
          <h2 className="text-xl font-bold text-brand-text">No Recording Selected for Separation</h2>
          <p className="text-xs text-brand-muted">
            Record a meeting or import an existing video to split Host and Guest into separate individual video files.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onImportFile}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white text-xs font-semibold shadow-glow transition-all active:scale-95"
          >
            <UploadCloud className="w-4 h-4" />
            <span>Import Video File</span>
          </button>
        </div>
      </div>
    );
  }

  // Local state for participants
  const [participants, setParticipants] = useState<ParticipantTrack[]>(session.participants || []);
  const [selectedParticipantId, setSelectedParticipantId] = useState<string>(
    session.participants?.[0]?.id || ''
  );

  // Playback state
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(session.durationSec || 0);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [showGridGuide, setShowGridGuide] = useState<boolean>(true);

  // Solo Preview Modal state
  const [soloPreviewParticipant, setSoloPreviewParticipant] = useState<ParticipantTrack | null>(null);
  const soloCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Export State
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportPhase, setExportPhase] = useState<string>('');
  const [exportProgress, setExportProgress] = useState<number>(0);
  const [exportCurrentItem, setExportCurrentItem] = useState<string>('');
  const [exportCompletedList, setExportCompletedList] = useState<string[]>([]);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);

  // AI Re-detection state
  const [isReDetecting, setIsReDetecting] = useState<boolean>(false);
  const [detectProgressMsg, setDetectProgressMsg] = useState<string>('');

  // Keep local participants in sync when session prop changes
  useEffect(() => {
    const currentParts = session.participants || [];
    setParticipants(currentParts);
    if (currentParts.length > 0) {
      if (!currentParts.some(p => p.id === selectedParticipantId)) {
        setSelectedParticipantId(currentParts[0].id);
      }
    }
  }, [session.id]);

  // Video element event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      if (video.duration && !isNaN(video.duration)) {
        setDuration(video.duration);
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
    };
  }, [session.blobUrl, session.filePath]);

  // Helper to commit participants changes to parent
  const commitParticipants = useCallback((newParticipants: ParticipantTrack[]) => {
    setParticipants(newParticipants);
    onUpdateSession({
      ...session,
      participants: newParticipants
    });
  }, [session, onUpdateSession]);

  // Toggle playback
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      video.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  // Seek
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
  };

  // Keyboard shortcut: Space = Play/Pause
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) {
        return;
      }
      if (e.code === 'Space') {
        e.preventDefault();
        togglePlay();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying]);

  // Layout presets handler - cleanly sets participant video crop regions
  const handleApplyPreset = (preset: LayoutPreset) => {
    const total = session.durationSec || 60;
    const newParticipants: ParticipantTrack[] = preset.tiles.map((tile, idx) => {
      const isHost = idx === 0;
      const role = isHost ? 'host' : 'attendee';
      const name = isHost ? 'Host' : `Guest ${idx}`;

      return {
        id: generateId('part'),
        name,
        role: role as 'host' | 'attendee' | 'presenter',
        crop: { ...tile },
        confidence: 95,
        isIncluded: true,
        visibilitySegments: [{ startSec: 0, endSec: total }]
      };
    });

    commitParticipants(newParticipants);
    if (newParticipants.length > 0) {
      setSelectedParticipantId(newParticipants[0].id);
    }
  };

  // Re-detect AI tiles from video
  const handleReDetectAI = async () => {
    if (!session.blobUrl && !session.filePath) return;
    setIsReDetecting(true);
    setDetectProgressMsg('Analyzing video frames for participant tiles...');

    try {
      const source = session.blobUrl || session.filePath;
      const result = await tileDetector.analyzeVideo(
        source,
        session.durationSec || 60,
        (msg) => setDetectProgressMsg(msg)
      );

      if (result.participants && result.participants.length > 0) {
        commitParticipants(result.participants);
        setSelectedParticipantId(result.participants[0].id);
      }
    } catch (e) {
      console.warn('AI tile re-detection error:', e);
    } finally {
      setIsReDetecting(false);
      setDetectProgressMsg('');
    }
  };

  // Add custom participant box
  const handleAddCustomBox = () => {
    const count = participants.length + 1;
    const newBox: ParticipantTrack = {
      id: generateId('part'),
      name: `Participant ${count}`,
      role: 'attendee',
      crop: { x: 0.1 * (count % 5), y: 0.1 * (count % 5), width: 0.4, height: 0.4 },
      confidence: 90,
      isIncluded: true,
      visibilitySegments: [{ startSec: 0, endSec: session.durationSec || 60 }]
    };
    const updated = [...participants, newBox];
    commitParticipants(updated);
    setSelectedParticipantId(newBox.id);
  };

  // Delete participant
  const handleDeleteParticipant = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (participants.length <= 1) return;
    const updated = participants.filter(p => p.id !== id);
    commitParticipants(updated);
    if (selectedParticipantId === id) {
      setSelectedParticipantId(updated[0]?.id || '');
    }
  };

  // Update participant single field
  const handleUpdateParticipant = (id: string, updates: Partial<ParticipantTrack>) => {
    const updated = participants.map(p => {
      if (p.id === id) {
        return { ...p, ...updates, manualCropAdjusted: updates.crop ? true : p.manualCropAdjusted };
      }
      return p;
    });
    commitParticipants(updated);
  };

  // Update crop for selected participant
  const handleCropChange = (id: string, newCrop: Partial<NormalizedRect>) => {
    const target = participants.find(p => p.id === id);
    if (!target) return;

    const merged: NormalizedRect = {
      x: clamp(newCrop.x !== undefined ? newCrop.x : target.crop.x, 0, 0.95),
      y: clamp(newCrop.y !== undefined ? newCrop.y : target.crop.y, 0, 0.95),
      width: clamp(newCrop.width !== undefined ? newCrop.width : target.crop.width, 0.05, 1),
      height: clamp(newCrop.height !== undefined ? newCrop.height : target.crop.height, 0.05, 1)
    };

    // Ensure within frame boundary
    if (merged.x + merged.width > 1) {
      merged.width = +(1 - merged.x).toFixed(4);
    }
    if (merged.y + merged.height > 1) {
      merged.height = +(1 - merged.y).toFixed(4);
    }

    handleUpdateParticipant(id, { crop: merged });
  };

  // Quick crop alignment presets
  const handleQuickCropPreset = (id: string, type: 'left_half' | 'right_half' | 'center_16_9' | 'center_9_16' | 'full') => {
    switch (type) {
      case 'left_half':
        handleCropChange(id, { x: 0.0, y: 0.0, width: 0.5, height: 1.0 });
        break;
      case 'right_half':
        handleCropChange(id, { x: 0.5, y: 0.0, width: 0.5, height: 1.0 });
        break;
      case 'center_16_9':
        handleCropChange(id, { x: 0.1, y: 0.1, width: 0.8, height: 0.8 });
        break;
      case 'center_9_16':
        handleCropChange(id, { x: 0.34, y: 0.0, width: 0.32, height: 1.0 });
        break;
      case 'full':
        handleCropChange(id, { x: 0.0, y: 0.0, width: 1.0, height: 1.0 });
        break;
    }
  };

  // Dragging and resizing interaction for bounding boxes on the video stage
  const dragInteractionRef = useRef<{
    participantId: string;
    mode: 'move' | 'nw' | 'ne' | 'se' | 'sw';
    startX: number;
    startY: number;
    initialCrop: NormalizedRect;
    containerWidth: number;
    containerHeight: number;
  } | null>(null);

  const startDragOrResize = (
    e: React.MouseEvent,
    pId: string,
    mode: 'move' | 'nw' | 'ne' | 'se' | 'sw'
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedParticipantId(pId);

    const container = containerRef.current;
    const participant = participants.find(p => p.id === pId);
    if (!container || !participant) return;

    const rect = container.getBoundingClientRect();
    dragInteractionRef.current = {
      participantId: pId,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      initialCrop: { ...participant.crop },
      containerWidth: rect.width,
      containerHeight: rect.height
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const state = dragInteractionRef.current;
      if (!state) return;

      const deltaX = (moveEvent.clientX - state.startX) / state.containerWidth;
      const deltaY = (moveEvent.clientY - state.startY) / state.containerHeight;
      const init = state.initialCrop;

      let newCrop = { ...init };

      if (state.mode === 'move') {
        newCrop.x = clamp(init.x + deltaX, 0, 1 - init.width);
        newCrop.y = clamp(init.y + deltaY, 0, 1 - init.height);
      } else if (state.mode === 'se') {
        newCrop.width = clamp(init.width + deltaX, 0.05, 1 - init.x);
        newCrop.height = clamp(init.height + deltaY, 0.05, 1 - init.y);
      } else if (state.mode === 'nw') {
        const potentialX = clamp(init.x + deltaX, 0, init.x + init.width - 0.05);
        const potentialY = clamp(init.y + deltaY, 0, init.y + init.height - 0.05);
        newCrop.width = init.width + (init.x - potentialX);
        newCrop.height = init.height + (init.y - potentialY);
        newCrop.x = potentialX;
        newCrop.y = potentialY;
      } else if (state.mode === 'ne') {
        newCrop.width = clamp(init.width + deltaX, 0.05, 1 - init.x);
        const potentialY = clamp(init.y + deltaY, 0, init.y + init.height - 0.05);
        newCrop.height = init.height + (init.y - potentialY);
        newCrop.y = potentialY;
      } else if (state.mode === 'sw') {
        const potentialX = clamp(init.x + deltaX, 0, init.x + init.width - 0.05);
        newCrop.width = init.width + (init.x - potentialX);
        newCrop.x = potentialX;
        newCrop.height = clamp(init.height + deltaY, 0.05, 1 - init.y);
      }

      handleCropChange(state.participantId, newCrop);
    };

    const handleMouseUp = () => {
      dragInteractionRef.current = null;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Solo Preview Canvas Render Loop
  useEffect(() => {
    if (!soloPreviewParticipant) return;
    const masterVideo = videoRef.current;
    const canvas = soloCanvasRef.current;
    if (!masterVideo || !canvas) return;

    let animId: number;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const renderSoloFrame = () => {
      if (masterVideo && canvas && ctx) {
        const vw = masterVideo.videoWidth || 1920;
        const vh = masterVideo.videoHeight || 1080;
        const crop = soloPreviewParticipant.crop;

        const sx = crop.x * vw;
        const sy = crop.y * vh;
        const sw = crop.width * vw;
        const sh = crop.height * vh;

        canvas.width = Math.max(10, Math.round(sw));
        canvas.height = Math.max(10, Math.round(sh));

        ctx.drawImage(masterVideo, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      }
      animId = requestAnimationFrame(renderSoloFrame);
    };

    renderSoloFrame();
    return () => {
      cancelAnimationFrame(animId);
    };
  }, [soloPreviewParticipant, isPlaying]);

  // Client-Side Canvas Crop & MediaRecorder Engine (Browser Fallback)
  const renderParticipantClipClientSide = async (
    participant: ParticipantTrack,
    onProgress: (pct: number) => void
  ): Promise<Blob> => {
    const video = document.createElement('video');
    video.src = session.blobUrl || session.filePath;
    video.crossOrigin = 'anonymous';
    video.muted = false;
    video.volume = 1.0;

    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = reject;
    });

    const vw = video.videoWidth || 1920;
    const vh = video.videoHeight || 1080;
    const crop = participant.crop;

    let targetW = Math.round(crop.width * vw);
    let targetH = Math.round(crop.height * vh);
    if (targetW % 2 !== 0) targetW--;
    if (targetH % 2 !== 0) targetH--;
    if (targetW < 2) targetW = 2;
    if (targetH < 2) targetH = 2;

    const offCanvas = document.createElement('canvas');
    offCanvas.width = targetW;
    offCanvas.height = targetH;
    const ctx = offCanvas.getContext('2d', { alpha: false, desynchronized: true })!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const stream = offCanvas.captureStream(session.fps || 30);

    // Capture standard audio from video element
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    let audioCtx: AudioContext | null = null;
    try {
      audioCtx = new AudioCtx({ sampleRate: 48000 });
      if (audioCtx.state === 'suspended') {
        await audioCtx.resume();
      }
      const sourceNode = audioCtx.createMediaElementSource(video);
      const destNode = audioCtx.createMediaStreamDestination();
      sourceNode.connect(destNode);
      const audioTracks = destNode.stream.getAudioTracks();
      if (audioTracks.length > 0) {
        stream.addTrack(audioTracks[0]);
      }
    } catch (e) {
      console.warn('Audio capture warning:', e);
    }

    const mime = MediaRecorder.isTypeSupported('video/mp4;codecs=avc1')
      ? 'video/mp4;codecs=avc1'
      : MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
      ? 'video/webm;codecs=vp8,opus'
      : 'video/webm';

    const recorder = new MediaRecorder(stream, {
      mimeType: mime,
      videoBitsPerSecond: 16_000_000,
      audioBitsPerSecond: 192_000
    });

    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    const completionPromise = new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        resolve(new Blob(chunks, { type: mime }));
        if (audioCtx) audioCtx.close().catch(() => {});
      };
    });

    recorder.start(100);
    video.currentTime = 0;
    await video.play();

    const totalDur = session.durationSec || video.duration || 10;
    const sx = crop.x * vw;
    const sy = crop.y * vh;
    const sw = crop.width * vw;
    const sh = crop.height * vh;

    const renderLoop = () => {
      if (video.ended || video.currentTime >= totalDur) {
        recorder.stop();
        video.pause();
        return;
      }

      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, targetW, targetH);
      const pct = Math.min(99, Math.round((video.currentTime / totalDur) * 100));
      onProgress(pct);
      requestAnimationFrame(renderLoop);
    };

    renderLoop();
    return await completionPromise;
  };

  // Single participant video export
  const handleExportSingleParticipant = async (participant: ParticipantTrack) => {
    setIsExporting(true);
    setShowExportModal(true);
    setExportPhase(`Separating ${participant.name} video...`);
    setExportProgress(10);
    setExportCurrentItem(`${participant.name}.mp4`);
    setExportCompletedList([]);

    const cleanTitle = session.title.replace(/[^a-zA-Z0-9_-]/g, '_');
    const safePartName = participant.name.replace(/[^a-zA-Z0-9_-]/g, '_');
    const fileName = `${cleanTitle}_${safePartName}.mp4`;

    try {
      if (window.electronAPI && session.filePath && typeof window.electronAPI.runFfmpegCrop === 'function') {
        const cropX = Math.round(participant.crop.x * session.resolution.width);
        const cropY = Math.round(participant.crop.y * session.resolution.height);
        let cropW = Math.round(participant.crop.width * session.resolution.width);
        let cropH = Math.round(participant.crop.height * session.resolution.height);
        if (cropW % 2 !== 0) cropW--;
        if (cropH % 2 !== 0) cropH--;

        setExportProgress(40);
        let baseDir = 'C:\\MeetSplitRecordings';
        if (session.filePath && session.filePath.includes('\\')) {
          baseDir = session.filePath.substring(0, session.filePath.lastIndexOf('\\'));
        }
        const outPath = `${baseDir}\\${fileName}`;

        await window.electronAPI.runFfmpegCrop({
          inputPath: session.filePath,
          outputPath: outPath,
          crop: { x: cropX, y: cropY, width: cropW, height: cropH },
          durationSec: session.durationSec,
          enhancedQuality: true
        });

        setExportProgress(100);
        setExportCompletedList([fileName]);
        if (typeof window.electronAPI.showItemInFolder === 'function') {
          window.electronAPI.showItemInFolder(outPath);
        }
      } else {
        const blob = await renderParticipantClipClientSide(participant, (pct) => {
          setExportProgress(pct);
        });
        downloadBlob(blob, fileName);
        setExportProgress(100);
        setExportCompletedList([fileName]);
      }

      confetti({ particleCount: 70, spread: 60, origin: { y: 0.7 } });
    } catch (e) {
      console.error('Participant export failed:', e);
      alert('Export encountered an issue. Please try again.');
    } finally {
      setIsExporting(false);
      setExportPhase('Video Separation Completed!');
    }
  };

  // Batch Export All Selected Participants
  const handleExportAllSeparateVideos = async () => {
    const included = participants.filter(p => p.isIncluded);
    if (included.length === 0) {
      alert('Please select at least one participant to separate.');
      return;
    }

    setIsExporting(true);
    setShowExportModal(true);
    setExportPhase(`Separating ${included.length} participant videos...`);
    setExportProgress(0);
    setExportCompletedList([]);

    const cleanTitle = session.title.replace(/[^a-zA-Z0-9_-]/g, '_');
    const completed: string[] = [];

    try {
      for (let i = 0; i < included.length; i++) {
        const p = included[i];
        const safeName = p.name.replace(/[^a-zA-Z0-9_-]/g, '_');
        const fileName = `${cleanTitle}_${safeName}.mp4`;

        setExportCurrentItem(fileName);
        setExportPhase(`Cropping video [${i + 1}/${included.length}]: ${p.name}...`);

        if (window.electronAPI && session.filePath && typeof window.electronAPI.runFfmpegCrop === 'function') {
          const cropX = Math.round(p.crop.x * session.resolution.width);
          const cropY = Math.round(p.crop.y * session.resolution.height);
          let cropW = Math.round(p.crop.width * session.resolution.width);
          let cropH = Math.round(p.crop.height * session.resolution.height);
          if (cropW % 2 !== 0) cropW--;
          if (cropH % 2 !== 0) cropH--;

          let baseDir = 'C:\\MeetSplitRecordings';
          if (session.filePath && session.filePath.includes('\\')) {
            baseDir = session.filePath.substring(0, session.filePath.lastIndexOf('\\'));
          }
          const outPath = `${baseDir}\\${fileName}`;

          await window.electronAPI.runFfmpegCrop({
            inputPath: session.filePath,
            outputPath: outPath,
            crop: { x: cropX, y: cropY, width: cropW, height: cropH },
            durationSec: session.durationSec,
            enhancedQuality: true
          });
          completed.push(fileName);
        } else {
          const blob = await renderParticipantClipClientSide(p, (subPct) => {
            const overall = Math.round(((i + subPct / 100) / included.length) * 100);
            setExportProgress(overall);
          });
          downloadBlob(blob, fileName);
          completed.push(fileName);
        }

        setExportCompletedList([...completed]);
        setExportProgress(Math.round(((i + 1) / included.length) * 100));
      }

      setExportPhase('All separate participant videos ready!');
      confetti({ particleCount: 100, spread: 80, origin: { y: 0.6 } });
    } catch (e) {
      console.error('Batch separate export failed:', e);
    } finally {
      setIsExporting(false);
    }
  };

  // Helper colors for participant boxes
  const getParticipantColor = (role?: string, index: number = 0) => {
    if (role === 'host') return { border: 'border-cyan-400', bg: 'bg-cyan-500/20', text: 'text-cyan-300' };
    if (role === 'presenter') return { border: 'border-indigo-400', bg: 'bg-indigo-500/20', text: 'text-indigo-300' };
    if (index % 2 === 1) return { border: 'border-purple-400', bg: 'bg-purple-500/20', text: 'text-purple-300' };
    return { border: 'border-amber-400', bg: 'bg-amber-500/20', text: 'text-amber-300' };
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-brand-bg select-none">
      {/* Top Header Bar */}
      <div className="h-14 px-6 border-b border-brand-border/60 bg-brand-surface/40 backdrop-blur-md flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-brand-accent/20 border border-brand-accent/40 flex items-center justify-center">
            <Users className="w-4 h-4 text-brand-accent" />
          </div>

          {/* Session Picker Dropdown */}
          <div className="relative group">
            <div className="flex items-center gap-2 cursor-pointer py-1 px-2.5 rounded-lg hover:bg-brand-elevated/60 transition-colors">
              <span className="text-xs font-bold text-brand-text max-w-[200px] truncate">
                {session.title}
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-elevated text-brand-muted font-mono">
                {formatDuration(session.durationSec)}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-brand-muted" />
            </div>

            {/* Dropdown Menu */}
            {recordings.length > 1 && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-brand-surface border border-brand-border rounded-xl shadow-elevated p-1.5 hidden group-hover:block z-50">
                <p className="text-[10px] font-semibold text-brand-muted px-2 py-1 uppercase tracking-wider">Switch Recording</p>
                {recordings.map((rec) => (
                  <button
                    key={rec.id}
                    onClick={() => onSelectSession(rec)}
                    className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs truncate flex items-center justify-between ${
                      rec.id === session.id
                        ? 'bg-brand-accent/20 text-brand-accent font-semibold'
                        : 'text-brand-muted hover:text-brand-text hover:bg-brand-elevated/60'
                    }`}
                  >
                    <span className="truncate">{rec.title}</span>
                    <span className="text-[10px] font-mono shrink-0 ml-2">{formatDuration(rec.durationSec)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2.5">
          {/* Open in Video Editor button */}
          <button
            onClick={onNavigateToEditor}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-xs text-brand-muted hover:text-brand-text transition-colors"
            title="Open in Video Editor"
          >
            <Scissors className="w-3.5 h-3.5 text-brand-accent" />
            <span>Open in Video Editor</span>
          </button>

          {/* Export Center Button */}
          <button
            onClick={onProceedToExport}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-xs text-brand-muted hover:text-brand-text transition-colors"
          >
            <Share2 className="w-3.5 h-3.5 text-brand-secondary" />
            <span>Export Center</span>
          </button>

          {/* Export All Separate Videos Button */}
          <button
            onClick={handleExportAllSeparateVideos}
            disabled={isExporting}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-gradient-to-r from-brand-accent to-brand-secondary hover:brightness-110 text-white text-xs font-bold shadow-glow transition-all active:scale-95 disabled:opacity-50"
          >
            {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            <span>Export Separate Videos</span>
          </button>
        </div>
      </div>

      {/* Main Studio Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left / Center: Video Stage & Interactive Overlays */}
        <div className="flex-1 flex flex-col p-4 md:p-6 overflow-hidden">
          {/* Layout Presets Toolbar */}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 bg-brand-surface/70 border border-brand-border/60 rounded-2xl px-3.5 py-2 shrink-0">
            <div className="flex items-center gap-1.5 overflow-x-auto">
              <span className="text-[11px] font-semibold text-brand-muted mr-1">Layout Presets:</span>
              {COMMON_LAYOUT_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => handleApplyPreset(preset)}
                  className="px-2.5 py-1 rounded-lg text-xs font-medium bg-brand-elevated hover:bg-brand-accent/20 hover:text-brand-accent border border-brand-border/60 transition-colors whitespace-nowrap"
                  title={preset.description}
                >
                  {preset.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              {/* AI Auto-detect Tiles */}
              <button
                onClick={handleReDetectAI}
                disabled={isReDetecting}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-brand-accent/15 text-brand-accent hover:bg-brand-accent/25 border border-brand-accent/30 transition-colors"
                title="Re-run AI tile analysis"
              >
                {isReDetecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                <span>Auto-Detect AI Tiles</span>
              </button>

              {/* Add Custom Tile */}
              <button
                onClick={handleAddCustomBox}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-brand-text transition-colors"
              >
                <Plus className="w-3.5 h-3.5 text-brand-secondary" />
                <span>Add Box</span>
              </button>

              {/* Toggle Guides */}
              <button
                onClick={() => setShowGridGuide(!showGridGuide)}
                className={`p-1.5 rounded-lg border text-xs transition-colors ${
                  showGridGuide ? 'bg-brand-accent/20 border-brand-accent/40 text-brand-accent' : 'bg-brand-elevated border-brand-border text-brand-muted'
                }`}
                title="Toggle grid alignment guides"
              >
                <Grid className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Interactive Video Stage */}
          <div className="flex-1 flex items-center justify-center min-h-0 bg-black/50 rounded-2xl border border-brand-border/80 overflow-hidden relative shadow-elevated">
            <div
              ref={containerRef}
              className="relative aspect-video max-h-full max-w-full flex items-center justify-center overflow-hidden"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            >
              {/* Master Video Tag with Natural Audio */}
              <video
                ref={videoRef}
                src={session.blobUrl || session.filePath}
                className="w-full h-full object-contain pointer-events-auto"
                playsInline
                muted={isMuted}
              />

              {/* Alignment Grid Overlay */}
              {showGridGuide && (
                <div className="absolute inset-0 pointer-events-none grid grid-cols-3 grid-rows-3 border border-white/5 opacity-40">
                  <div className="border-r border-b border-white/10" />
                  <div className="border-r border-b border-white/10" />
                  <div className="border-b border-white/10" />
                  <div className="border-r border-b border-white/10" />
                  <div className="border-r border-b border-white/10" />
                  <div className="border-b border-white/10" />
                  <div className="border-r border-b border-white/10" />
                  <div className="border-r border-b border-white/10" />
                  <div />
                </div>
              )}

              {/* Interactive Participant Bounding Boxes */}
              {participants.map((p, idx) => {
                const isSelected = p.id === selectedParticipantId;
                const colors = getParticipantColor(p.role, idx);

                return (
                  <div
                    key={p.id}
                    onClick={() => setSelectedParticipantId(p.id)}
                    className={`absolute cursor-move transition-[border-color,box-shadow] group ${
                      isSelected
                        ? `border-2 ${colors.border} shadow-[0_0_15px_rgba(0,245,212,0.4)] z-30`
                        : `border-2 border-dashed ${colors.border} opacity-80 hover:opacity-100 z-20`
                    }`}
                    style={{
                      left: `${p.crop.x * 100}%`,
                      top: `${p.crop.y * 100}%`,
                      width: `${p.crop.width * 100}%`,
                      height: `${p.crop.height * 100}%`,
                    }}
                    onMouseDown={(e) => startDragOrResize(e, p.id, 'move')}
                  >
                    {/* Participant Badge Header */}
                    <div className={`absolute top-0 left-0 -translate-y-full px-2 py-0.5 rounded-t-md text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 select-none ${colors.bg} ${colors.text} border-t border-x ${colors.border}`}>
                      <span>{p.role === 'host' ? '🎙️ Host' : '👥 Guest'}: {p.name}</span>
                    </div>

                    {/* Crop % Dimensions Indicator */}
                    <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/80 text-[9px] font-mono text-white/90 pointer-events-none">
                      {Math.round(p.crop.width * 100)}% × {Math.round(p.crop.height * 100)}%
                    </div>

                    {/* Resize Handles (Only for selected) */}
                    {isSelected && (
                      <>
                        <div
                          className="absolute -top-1.5 -left-1.5 w-3.5 h-3.5 bg-white border-2 border-brand-accent rounded-full cursor-nwse-resize z-40 hover:scale-125 transition-transform"
                          onMouseDown={(e) => startDragOrResize(e, p.id, 'nw')}
                        />
                        <div
                          className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-white border-2 border-brand-accent rounded-full cursor-nesw-resize z-40 hover:scale-125 transition-transform"
                          onMouseDown={(e) => startDragOrResize(e, p.id, 'ne')}
                        />
                        <div
                          className="absolute -bottom-1.5 -left-1.5 w-3.5 h-3.5 bg-white border-2 border-brand-accent rounded-full cursor-nesw-resize z-40 hover:scale-125 transition-transform"
                          onMouseDown={(e) => startDragOrResize(e, p.id, 'sw')}
                        />
                        <div
                          className="absolute -bottom-1.5 -right-1.5 w-3.5 h-3.5 bg-white border-2 border-brand-accent rounded-full cursor-nwse-resize z-40 hover:scale-125 transition-transform"
                          onMouseDown={(e) => startDragOrResize(e, p.id, 'se')}
                        />
                      </>
                    )}
                  </div>
                );
              })}

              {/* Status banner when re-detecting */}
              {isReDetecting && (
                <div className="absolute inset-0 bg-black/75 backdrop-blur-sm flex flex-col items-center justify-center gap-3 z-50 text-white">
                  <Loader2 className="w-8 h-8 text-brand-accent animate-spin" />
                  <p className="text-xs font-semibold">{detectProgressMsg}</p>
                </div>
              )}
            </div>
          </div>

          {/* Clean Playback Controls & Scrubber */}
          <div className="mt-3 bg-brand-surface/80 border border-brand-border/60 rounded-2xl p-3 flex items-center gap-3 shrink-0">
            <button
              onClick={togglePlay}
              className="w-8 h-8 rounded-xl bg-brand-accent text-white flex items-center justify-center shadow-glow hover:bg-brand-accent-hover transition-all active:scale-95 shrink-0"
              title={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>

            <button
              onClick={() => {
                if (videoRef.current) {
                  videoRef.current.currentTime = 0;
                  setCurrentTime(0);
                }
              }}
              className="p-1.5 rounded-lg text-brand-muted hover:text-brand-text hover:bg-brand-elevated transition-colors shrink-0"
              title="Restart from beginning"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            {/* Timecode */}
            <span className="text-xs font-mono text-brand-text tabular-nums shrink-0">
              {formatDuration(currentTime)} / {formatDuration(duration)}
            </span>

            {/* Seek Bar */}
            <div className="flex-1 relative flex items-center">
              <input
                type="range"
                min={0}
                max={duration || 100}
                step={0.1}
                value={currentTime}
                onChange={handleSeek}
                className="w-full h-1.5 bg-brand-elevated rounded-full accent-brand-accent cursor-pointer border border-brand-border/40"
              />
            </div>

            {/* Mute Button */}
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="p-1.5 rounded-lg text-brand-muted hover:text-brand-text hover:bg-brand-elevated transition-colors shrink-0"
              title={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? <VolumeX className="w-4 h-4 text-red-400" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Right Panel: Detected Participants & Video Separation Controls */}
        <div className="w-80 md:w-96 border-l border-brand-border/60 bg-brand-surface/40 flex flex-col overflow-hidden shrink-0">
          <div className="p-4 border-b border-brand-border/60 flex items-center justify-between">
            <div>
              <h3 className="text-xs font-bold text-brand-text uppercase tracking-wider flex items-center gap-2">
                <Users className="w-4 h-4 text-brand-accent" />
                <span>Participants ({participants.length})</span>
              </h3>
              <p className="text-[10px] text-brand-muted mt-0.5">Adjust crop boxes to separate each video</p>
            </div>

            <div className="flex items-center gap-1.5 text-[10px]">
              <button
                onClick={() => {
                  const updated = participants.map(p => ({ ...p, isIncluded: true }));
                  commitParticipants(updated);
                }}
                className="text-brand-accent hover:underline font-semibold"
              >
                All
              </button>
              <span className="text-brand-border">|</span>
              <button
                onClick={() => {
                  const updated = participants.map(p => ({ ...p, isIncluded: false }));
                  commitParticipants(updated);
                }}
                className="text-brand-muted hover:text-brand-text"
              >
                None
              </button>
            </div>
          </div>

          {/* Participant Cards Scrollable List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3.5">
            {participants.map((p, idx) => {
              const isSelected = p.id === selectedParticipantId;
              const colors = getParticipantColor(p.role, idx);

              return (
                <div
                  key={p.id}
                  onClick={() => setSelectedParticipantId(p.id)}
                  className={`p-3.5 rounded-2xl border transition-all cursor-pointer ${
                    isSelected
                      ? `bg-brand-surface border-brand-accent shadow-md ring-1 ring-brand-accent/50`
                      : 'bg-brand-surface/60 hover:bg-brand-surface border-brand-border/60'
                  }`}
                >
                  {/* Top Row: Inclusion Checkbox + Name + Role + Delete */}
                  <div className="flex items-center justify-between gap-2 mb-2.5">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpdateParticipant(p.id, { isIncluded: !p.isIncluded });
                        }}
                        className="text-brand-accent hover:scale-110 transition-transform shrink-0"
                        title={p.isIncluded ? 'Included in export' : 'Excluded from export'}
                      >
                        {p.isIncluded ? (
                          <CheckSquare className="w-4 h-4 text-brand-accent" />
                        ) : (
                          <Square className="w-4 h-4 text-brand-muted" />
                        )}
                      </button>

                      <input
                        type="text"
                        value={p.name}
                        onChange={(e) => handleUpdateParticipant(p.id, { name: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-transparent border-b border-transparent hover:border-brand-border focus:border-brand-accent text-xs font-bold text-brand-text px-1 py-0.5 rounded outline-none w-full"
                        placeholder="Participant Name"
                      />
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {/* Role Selector Badge */}
                      <select
                        value={p.role || 'attendee'}
                        onChange={(e) => handleUpdateParticipant(p.id, { role: e.target.value as any })}
                        onClick={(e) => e.stopPropagation()}
                        className={`text-[10px] font-bold rounded-lg px-2 py-0.5 outline-none cursor-pointer border ${colors.bg} ${colors.text} ${colors.border}`}
                      >
                        <option value="host">Host</option>
                        <option value="attendee">Guest</option>
                        <option value="presenter">Presenter</option>
                      </select>

                      {participants.length > 1 && (
                        <button
                          onClick={(e) => handleDeleteParticipant(p.id, e)}
                          className="p-1 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Remove this box"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Quick Alignment Presets for this box */}
                  <div className="mb-3 flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleQuickCropPreset(p.id, 'left_half'); }}
                      className="flex-1 py-1 px-1.5 rounded bg-brand-elevated hover:bg-brand-accent/20 hover:text-brand-accent text-[9px] font-semibold text-brand-muted transition-colors text-center"
                    >
                      Left Half
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleQuickCropPreset(p.id, 'right_half'); }}
                      className="flex-1 py-1 px-1.5 rounded bg-brand-elevated hover:bg-brand-accent/20 hover:text-brand-accent text-[9px] font-semibold text-brand-muted transition-colors text-center"
                    >
                      Right Half
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleQuickCropPreset(p.id, 'center_9_16'); }}
                      className="flex-1 py-1 px-1.5 rounded bg-brand-elevated hover:bg-brand-accent/20 hover:text-brand-accent text-[9px] font-semibold text-brand-muted transition-colors text-center"
                    >
                      9:16 Shorts
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleQuickCropPreset(p.id, 'full'); }}
                      className="flex-1 py-1 px-1.5 rounded bg-brand-elevated hover:bg-brand-accent/20 hover:text-brand-accent text-[9px] font-semibold text-brand-muted transition-colors text-center"
                    >
                      Full Frame
                    </button>
                  </div>

                  {/* Crop Coordinates Sliders (Only if selected) */}
                  {isSelected && (
                    <div className="pt-2 border-t border-brand-border/40 grid grid-cols-2 gap-2 text-[10px]">
                      <div>
                        <span className="text-brand-muted">Left (X): {Math.round(p.crop.x * 100)}%</span>
                        <input
                          type="range"
                          min={0}
                          max={0.9}
                          step={0.01}
                          value={p.crop.x}
                          onChange={(e) => handleCropChange(p.id, { x: parseFloat(e.target.value) })}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full h-1 bg-brand-elevated rounded accent-brand-accent"
                        />
                      </div>
                      <div>
                        <span className="text-brand-muted">Top (Y): {Math.round(p.crop.y * 100)}%</span>
                        <input
                          type="range"
                          min={0}
                          max={0.9}
                          step={0.01}
                          value={p.crop.y}
                          onChange={(e) => handleCropChange(p.id, { y: parseFloat(e.target.value) })}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full h-1 bg-brand-elevated rounded accent-brand-accent"
                        />
                      </div>
                      <div>
                        <span className="text-brand-muted">Width: {Math.round(p.crop.width * 100)}%</span>
                        <input
                          type="range"
                          min={0.1}
                          max={1}
                          step={0.01}
                          value={p.crop.width}
                          onChange={(e) => handleCropChange(p.id, { width: parseFloat(e.target.value) })}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full h-1 bg-brand-elevated rounded accent-brand-accent"
                        />
                      </div>
                      <div>
                        <span className="text-brand-muted">Height: {Math.round(p.crop.height * 100)}%</span>
                        <input
                          type="range"
                          min={0.1}
                          max={1}
                          step={0.01}
                          value={p.crop.height}
                          onChange={(e) => handleCropChange(p.id, { height: parseFloat(e.target.value) })}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full h-1 bg-brand-elevated rounded accent-brand-accent"
                        />
                      </div>
                    </div>
                  )}

                  {/* Actions: Solo Preview & Single Export */}
                  <div className="mt-3 pt-2.5 border-t border-brand-border/40 flex items-center justify-between gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSoloPreviewParticipant(p);
                      }}
                      className="flex items-center gap-1.5 text-[11px] font-semibold text-brand-secondary hover:text-brand-secondary/80 py-1 px-2 rounded-lg hover:bg-brand-secondary/10 transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>Solo Live Preview</span>
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExportSingleParticipant(p);
                      }}
                      className="flex items-center gap-1 text-[11px] font-semibold text-brand-accent hover:text-brand-accent-hover py-1 px-2 rounded-lg hover:bg-brand-accent/10 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>Export Video</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom Summary & Batch Export */}
          <div className="p-4 border-t border-brand-border/60 bg-brand-surface/70 space-y-2.5">
            <div className="flex items-center justify-between text-xs text-brand-muted">
              <span>Selected to separate:</span>
              <span className="font-bold text-brand-text">
                {participants.filter(p => p.isIncluded).length} of {participants.length} videos
              </span>
            </div>

            <button
              onClick={handleExportAllSeparateVideos}
              disabled={isExporting}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-brand-accent to-brand-secondary text-white text-xs font-bold shadow-glow hover:brightness-110 transition-all active:scale-95 disabled:opacity-50"
            >
              {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span>Split & Export All Separate Videos</span>
            </button>
          </div>
        </div>
      </div>

      {/* Solo Live Preview Modal */}
      {soloPreviewParticipant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4">
          <div className="bg-brand-surface border border-brand-border rounded-3xl max-w-3xl w-full p-6 shadow-elevated space-y-4 max-h-[92vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex flex-wrap items-center justify-between pb-3 border-b border-brand-border/60 gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-brand-accent/20 text-brand-accent flex items-center justify-center shadow-glow">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-brand-text flex items-center gap-2">
                    <span>Solo Live Preview</span>
                  </h3>
                  <p className="text-[11px] text-brand-muted">
                    Previewing {soloPreviewParticipant.name} ({soloPreviewParticipant.role?.toUpperCase()}) cropped video
                  </p>
                </div>
              </div>

              {/* Speaker Switcher Pills inside Modal */}
              <div className="flex items-center gap-1.5 bg-brand-elevated/80 p-1 rounded-xl border border-brand-border/60">
                {participants.map((p) => {
                  const isCur = soloPreviewParticipant.id === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setSoloPreviewParticipant(p)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                        isCur
                          ? 'bg-brand-accent text-white shadow-glow'
                          : 'text-brand-muted hover:text-brand-text hover:bg-brand-elevated'
                      }`}
                    >
                      <span>{p.role === 'host' ? '🎙️ Host' : '👥 Guest'}</span>
                      <span className="truncate max-w-[90px] font-normal">({p.name})</span>
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setSoloPreviewParticipant(null)}
                className="p-1.5 rounded-xl text-brand-muted hover:text-brand-text hover:bg-brand-elevated transition-colors ml-auto"
                title="Close Preview"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Live Cropped Canvas Video Stage */}
            <div className="w-full aspect-video bg-black/95 rounded-2xl overflow-hidden flex items-center justify-center border border-brand-border/60 relative group">
              <canvas
                ref={soloCanvasRef}
                className="max-w-full max-h-full object-contain"
              />

              {/* Crop Watermark Badge */}
              <div className="absolute top-3 left-3 px-2.5 py-1 rounded-xl bg-black/70 backdrop-blur-md border border-white/10 text-[10px] font-semibold text-white/90 flex items-center gap-1.5">
                <User className="w-3 h-3 text-brand-accent" />
                <span>Solo Crop: {Math.round(soloPreviewParticipant.crop.width * 100)}% × {Math.round(soloPreviewParticipant.crop.height * 100)}%</span>
              </div>

              {/* Play / Pause Overlay on Video Click */}
              <div
                onClick={togglePlay}
                className="absolute inset-0 cursor-pointer flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20"
              >
                <div className="w-12 h-12 rounded-2xl bg-black/70 backdrop-blur-md flex items-center justify-center text-white shadow-elevated">
                  {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
                </div>
              </div>
            </div>

            {/* Modal Scrubber & Playback Controls */}
            <div className="bg-brand-surface/90 border border-brand-border/70 rounded-2xl p-3 flex items-center gap-3">
              <button
                onClick={togglePlay}
                className="w-9 h-9 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white flex items-center justify-center shadow-glow transition-all active:scale-95 shrink-0"
                title={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
              </button>

              <button
                onClick={() => {
                  if (videoRef.current) {
                    videoRef.current.currentTime = 0;
                    setCurrentTime(0);
                  }
                }}
                className="p-1.5 rounded-lg text-brand-muted hover:text-brand-text hover:bg-brand-elevated transition-colors shrink-0"
                title="Restart from beginning"
              >
                <RotateCcw className="w-4 h-4" />
              </button>

              {/* Timecode */}
              <span className="text-xs font-mono text-brand-text tabular-nums shrink-0">
                {formatDuration(currentTime)} / {formatDuration(duration)}
              </span>

              {/* Visual Scrubber Track */}
              <div className="flex-1 relative flex items-center">
                <input
                  type="range"
                  min={0}
                  max={duration || 100}
                  step={0.1}
                  value={currentTime}
                  onChange={handleSeek}
                  className="w-full h-1.5 bg-brand-elevated rounded-full accent-brand-accent cursor-pointer border border-brand-border/40"
                />
              </div>

              {/* Mute Button */}
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="p-1.5 rounded-lg text-brand-muted hover:text-brand-text hover:bg-brand-elevated transition-colors shrink-0"
                title={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? <VolumeX className="w-4 h-4 text-red-400" /> : <Volume2 className="w-4 h-4" />}
              </button>
            </div>

            {/* Modal Bottom Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-brand-border/40">
              <button
                onClick={() => setSoloPreviewParticipant(null)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-brand-muted hover:text-brand-text hover:bg-brand-elevated transition-colors"
              >
                Close Preview
              </button>

              <button
                onClick={() => {
                  handleExportSingleParticipant(soloPreviewParticipant);
                  setSoloPreviewParticipant(null);
                }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-brand-accent to-brand-secondary hover:brightness-110 text-white text-xs font-bold shadow-glow transition-all active:scale-95"
              >
                <Download className="w-4 h-4" />
                <span>Download This Separate Video</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Progress & Notification Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
          <div className="bg-brand-surface border border-brand-border rounded-3xl max-w-md w-full p-6 shadow-elevated space-y-5">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-brand-accent to-brand-secondary flex items-center justify-center mx-auto shadow-glow">
                {exportProgress >= 100 ? (
                  <CheckCircle2 className="w-6 h-6 text-white" />
                ) : (
                  <Download className="w-6 h-6 text-white animate-bounce" />
                )}
              </div>

              <h3 className="text-base font-bold text-brand-text">
                {exportProgress >= 100 ? 'Separate Videos Exported Successfully!' : 'Exporting Separate Videos'}
              </h3>

              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[10px] font-semibold">
                <Sparkles className="w-3 h-3" />
                <span>Zero Quality Loss • Crisp Visual Output</span>
              </div>

              <p className="text-xs text-brand-muted">
                {exportPhase}
              </p>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-brand-text truncate max-w-[200px]">{exportCurrentItem}</span>
                <span className="font-mono text-brand-accent">{exportProgress}%</span>
              </div>

              <div className="w-full h-2.5 bg-brand-elevated rounded-full overflow-hidden p-0.5 border border-brand-border/60">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-brand-accent to-brand-secondary transition-all duration-300"
                  style={{ width: `${Math.max(5, exportProgress)}%` }}
                />
              </div>
            </div>

            {/* Completed files list */}
            {exportCompletedList.length > 0 && (
              <div className="p-3 rounded-xl bg-brand-elevated/40 border border-brand-border/40 space-y-1 max-h-32 overflow-y-auto">
                <span className="text-[10px] font-semibold text-brand-muted uppercase tracking-wider block">Ready Files:</span>
                {exportCompletedList.map((file, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-emerald-400 font-mono">
                    <Check className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{file}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Modal Buttons */}
            <div className="flex items-center justify-end gap-2 pt-2">
              {exportProgress >= 100 ? (
                <button
                  onClick={() => setShowExportModal(false)}
                  className="px-5 py-2 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white text-xs font-bold shadow-glow transition-all"
                >
                  Done
                </button>
              ) : (
                <button
                  onClick={() => {
                    setIsExporting(false);
                    setShowExportModal(false);
                  }}
                  className="px-4 py-2 rounded-xl text-xs text-brand-muted hover:text-red-400"
                >
                  Cancel Export
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
